import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { DocLangHint } from './DocLang';

// Uch tilli hujjat ma'lumotnomasi (backend: app/routers/document_phrases.py).
// Ro'yxatdan tanlangan matn hujjatga uz/ru/en NUSXASI bilan yoziladi - har bir
// til PDF'da o'z variantini oladi. Ro'yxatda yo'q bo'lsa - erkin matn (hujjat
// tilida, "Hujjat tili" ogohlantirishi). Ro'yxatni admin/direktor tahrirlaydi.
export type PhraseKind = 'position' | 'work_type' | 'checklist';
export interface Phrase { id: number; kind: PhraseKind; key: string | null; text_uz: string; text_ru: string; text_en: string; sort_order: number; }
export interface I18nText { uz: string; ru: string; en: string; }

export const phraseI18n = (p: Phrase): I18nText => ({ uz: p.text_uz, ru: p.text_ru, en: p.text_en });
export const phraseText = (p: Phrase, lang?: string | null) =>
  (lang === 'ru' ? p.text_ru : lang === 'en' ? p.text_en : p.text_uz);
export const i18nText = (t: I18nText | null | undefined, lang?: string | null) =>
  (t ? (lang === 'ru' ? t.ru : lang === 'en' ? t.en : t.uz) : '');
export const sameI18n = (a?: I18nText | null, b?: I18nText | null) =>
  !!a && !!b && a.uz === b.uz && a.ru === b.ru && a.en === b.en;

export const usePhrases = (): Phrase[] => {
  const [phrases, setPhrases] = useState<Phrase[]>([]);
  useEffect(() => {
    api.get<Phrase[]>('/document-phrases').then(r => setPhrases(r.data)).catch(() => setPhrases([]));
  }, []);
  return phrases;
};

const FREE = '__free__';

// Lavozim: ro'yxatdan tanlash yoki erkin matn.
export const PhraseField: React.FC<{
  label: string; lang?: string | null; phrases: Phrase[]; kind: PhraseKind;
  text?: string | null; i18n?: I18nText | null; placeholder?: string;
  onChange: (text: string, i18n: I18nText | null) => void;
}> = ({ label, lang, phrases, kind, text, i18n, placeholder, onChange }) => {
  const list = phrases.filter(p => p.kind === kind);
  const selected = i18n ? list.find(p => sameI18n(phraseI18n(p), i18n)) : undefined;
  const [free, setFree] = useState<boolean>(!i18n && !!(text || '').trim());
  const value = selected ? String(selected.id) : (i18n ? '__snapshot__' : (free ? FREE : ''));
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      <select value={value} className="w-full border rounded px-2 py-1"
        onChange={e => {
          const v = e.target.value;
          if (v === FREE) { setFree(true); onChange(i18n ? '' : (text || ''), null); return; }
          setFree(false);
          const p = list.find(x => String(x.id) === v);
          if (p) onChange(phraseText(p, lang), phraseI18n(p)); else onChange('', null);
        }}>
        <option value="">— tanlang —</option>
        {list.map(p => <option key={p.id} value={p.id}>{phraseText(p, lang)}</option>)}
        {i18n && !selected && <option value="__snapshot__">{i18nText(i18n, lang)}</option>}
        <option value={FREE}>Boshqa (erkin matn)…</option>
      </select>
      {value === FREE && (
        <>
          <input value={text || ''} onChange={e => onChange(e.target.value, null)} placeholder={placeholder}
            className="mt-1 w-full border rounded px-2 py-1" />
          <DocLangHint lang={lang} />
        </>
      )}
    </div>
  );
};

const KIND_LABELS: Record<PhraseKind, string> = {
  position: 'Vakil lavozimi', work_type: 'Dalolatnoma: ish turlari', checklist: 'Dalolatnoma: tekshiruv natijasi qatorlari',
};

// Sozlamalar sahifasi uchun: ro'yxatni tahrirlash (admin/direktor).
export const DocumentPhrasesEditor: React.FC = () => {
  const [rows, setRows] = useState<Phrase[]>([]);
  const [draft, setDraft] = useState<Record<string, I18nText>>({});
  const [error, setError] = useState('');
  const load = async () => {
    try { const r = await api.get<Phrase[]>('/document-phrases'); setRows(r.data); } catch { setError("Ma'lumotnomani yuklab bo'lmadi"); }
  };
  useEffect(() => { load(); }, []);

  const saveRow = async (p: Phrase) => {
    setError('');
    try { await api.put(`/document-phrases/${p.id}`, { text_uz: p.text_uz, text_ru: p.text_ru, text_en: p.text_en }); await load(); }
    catch (e: any) { setError(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Xato'); }
  };
  const removeRow = async (p: Phrase) => {
    if (!window.confirm(`"${p.text_uz}" ro'yxatdan o'chirilsinmi? (Berilgan hujjatlar o'zgarmaydi)`)) return;
    try { await api.delete(`/document-phrases/${p.id}`); await load(); } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
  };
  const addRow = async (kind: PhraseKind) => {
    const d = draft[kind] || { uz: '', ru: '', en: '' };
    if (!d.uz.trim() || !d.ru.trim() || !d.en.trim()) { setError("Uchala tildagi matn ham kerak"); return; }
    setError('');
    try {
      await api.post('/document-phrases', { kind, text_uz: d.uz, text_ru: d.ru, text_en: d.en });
      setDraft({ ...draft, [kind]: { uz: '', ru: '', en: '' } }); await load();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
  };
  const patch = (id: number, field: 'text_uz' | 'text_ru' | 'text_en', value: string) =>
    setRows(rows.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  const inputCls = 'w-full border rounded px-2 py-1 text-xs';

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">
        Takrorlanadigan matnlar uch tilda. Hujjatda ro&apos;yxatdan tanlansa - uz/ru/en PDF har biri o&apos;z tilidagi variantni oladi.
        Tahrir faqat keyingi tanlovlarga ta&apos;sir qiladi (berilgan hujjatlarda tanlangan paytdagi matn saqlanadi).
      </p>
      {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-2 text-xs">{error}</div>}
      {(Object.keys(KIND_LABELS) as PhraseKind[]).map(kind => (
        <div key={kind} className="border rounded-lg p-3 bg-gray-50 mb-3">
          <p className="text-xs font-semibold text-gray-600 mb-2">{KIND_LABELS[kind]}</p>
          <div className="hidden md:grid grid-cols-12 gap-2 text-[11px] text-gray-400 mb-1">
            <span className="col-span-3">🇺🇿 O&apos;zbekcha</span><span className="col-span-3">🇷🇺 Ruscha</span><span className="col-span-3">🇬🇧 Inglizcha</span>
          </div>
          <div className="space-y-1">
            {rows.filter(r => r.kind === kind).map(r => (
              <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                <input value={r.text_uz} onChange={e => patch(r.id, 'text_uz', e.target.value)} className={`md:col-span-3 ${inputCls}`} />
                <input value={r.text_ru} onChange={e => patch(r.id, 'text_ru', e.target.value)} className={`md:col-span-3 ${inputCls}`} />
                <input value={r.text_en} onChange={e => patch(r.id, 'text_en', e.target.value)} className={`md:col-span-3 ${inputCls}`} />
                <div className="md:col-span-3 flex gap-1">
                  <button type="button" onClick={() => saveRow(r)} className="px-2 py-1 text-xs border rounded hover:bg-white">Saqlash</button>
                  <button type="button" onClick={() => removeRow(r)} className="px-2 py-1 text-xs border rounded text-red-500 hover:bg-red-50">✕</button>
                </div>
              </div>
            ))}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center pt-1 border-t mt-1">
              {(['uz', 'ru', 'en'] as const).map(l => (
                <input key={l} value={(draft[kind] || { uz: '', ru: '', en: '' })[l]} placeholder={`Yangi (${l.toUpperCase()})`}
                  onChange={e => setDraft({ ...draft, [kind]: { ...(draft[kind] || { uz: '', ru: '', en: '' }), [l]: e.target.value } })}
                  className={`md:col-span-3 ${inputCls}`} />
              ))}
              <div className="md:col-span-3">
                <button type="button" onClick={() => addRow(kind)} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">+ Qo&apos;shish</button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
